import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormLabel,
  Button as MuiButton,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useNotify, useRecordContext, useRefresh } from 'react-admin';
import { DEFAULT_API_ADMIN } from '../../constants';
import { httpClientReactAdmin } from '../../lib';

interface MigrationOptions {
  migrateScoring: boolean;
  migrateDiscounts: boolean;
  enableScoringRules: boolean;
  overwriteExisting: boolean;
}

interface MigrationSummary {
  groupsMigrated: number;
  tagsCreated: number;
  customersTagged: number;
  scoringsCreated: number;
  scoringsUpdated: number;
  discountsRetargeted: number;
  dryRun: boolean;
}

const DEFAULT_OPTIONS: MigrationOptions = {
  migrateScoring: true,
  migrateDiscounts: true,
  enableScoringRules: true,
  overwriteExisting: false,
};

const plural = (count: number, singular: string, pluralWord: string) =>
  `${count} ${count === 1 ? singular : pluralWord}`;

const describe = (summary: MigrationSummary) =>
  [
    plural(summary.tagsCreated, 'etiqueta criada', 'etiquetas criadas'),
    plural(summary.customersTagged, 'cliente etiquetado', 'clientes etiquetados'),
    plural(summary.scoringsCreated, 'regra de pontuação criada', 'regras de pontuação criadas'),
    ...(summary.scoringsUpdated
      ? [plural(summary.scoringsUpdated, 'regra atualizada', 'regras atualizadas')]
      : []),
    plural(summary.discountsRetargeted, 'desconto apontado pra etiqueta', 'descontos apontados pra etiqueta'),
  ].join(', ');

export const MigrateGroupsToTagsDialog: React.FC = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<MigrationOptions>(DEFAULT_OPTIONS);
  const [preview, setPreview] = useState<MigrationSummary | null>(null);

  if (!record?.id) return null;

  const toggle = (field: keyof MigrationOptions) => (_: unknown, checked: boolean) => {
    setOptions(current => ({ ...current, [field]: checked }));
    setPreview(null);
  };

  const closeDialog = () => {
    setOpen(false);
    setOptions(DEFAULT_OPTIONS);
    setPreview(null);
  };

  const migrate = async (dryRun: boolean) => {
    setLoading(true);
    try {
      const { json } = await httpClientReactAdmin(`${DEFAULT_API_ADMIN}/chains/${record.id}/migrate-groups-to-tags`, {
        method: 'POST',
        body: JSON.stringify({ ...options, dryRun }),
      });
      const summary: MigrationSummary = json?.data?.[0] ?? json;

      if (dryRun) {
        setPreview(summary);
        return;
      }

      notify(`Migração concluída: ${describe(summary)}.`, { type: 'success' });
      refresh();
      closeDialog();
    } catch (err: any) {
      const message = err?.body?.message || err?.message || 'Erro ao migrar grupos para etiquetas';
      notify(message, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ my: 1 }}>
      <MuiButton variant="outlined" color="primary" disabled={loading} onClick={() => setOpen(true)}>
        Migrar grupos pra etiqueta
      </MuiButton>

      <Dialog open={open} onClose={loading ? undefined : closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Migrar grupos pra etiqueta</DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Cria uma etiqueta por grupo desta rede e leva junto o que tu marcar abaixo. É aditivo: os grupos atuais
            continuam funcionando do jeito que estão.
          </Typography>

          <FormLabel component="legend" sx={{ fontWeight: 'bold' }}>O que migrar</FormLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
            <FormControlLabel
              control={<Checkbox checked disabled />}
              label="Membros do grupo → etiqueta"
            />
            <FormControlLabel
              control={<Checkbox checked={options.migrateScoring} onChange={toggle('migrateScoring')} />}
              label="Pontuação (fator por combustível)"
            />
            <FormControlLabel
              control={<Checkbox checked={options.migrateDiscounts} onChange={toggle('migrateDiscounts')} />}
              label="Descontos que hoje miram o grupo"
            />
            <FormControlLabel
              control={<Checkbox checked={false} disabled />}
              label="Ver preço no app 🔒 requer desenvolvimento"
            />
          </Box>

          <Divider />

          {options.migrateScoring && (
            <Box sx={{ mt: 2 }}>
              <FormLabel component="legend" sx={{ fontWeight: 'bold' }}>
                Quando a etiqueta já tem regra de pontuação
              </FormLabel>
              <RadioGroup
                value={options.overwriteExisting ? 'overwrite' : 'skip'}
                onChange={(_, value) => {
                  setOptions(current => ({ ...current, overwriteExisting: value === 'overwrite' }));
                  setPreview(null);
                }}
              >
                <FormControlLabel value="skip" control={<Radio />} label="Pular (não mexe na regra existente)" />
                <FormControlLabel value="overwrite" control={<Radio />} label="Atualizar os fatores pelo grupo" />
              </RadioGroup>
            </Box>
          )}

          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Checkbox checked={options.enableScoringRules} onChange={toggle('enableScoringRules')} />}
            label='Ligar "usar novas regras de pontuação" nesta rede'
          />

          {preview && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>Prévia — nada foi gravado.</strong>
              <br />
              {preview.groupsMigrated
                ? `${plural(preview.groupsMigrated, 'grupo analisado', 'grupos analisados')}: ${describe(preview)}.`
                : 'Esta rede não tem grupo nenhum pra migrar.'}
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <MuiButton onClick={() => migrate(true)} disabled={loading}>
            Simular
          </MuiButton>
          <MuiButton onClick={closeDialog} disabled={loading}>
            Cancelar
          </MuiButton>
          <MuiButton variant="contained" onClick={() => migrate(false)} disabled={loading}>
            {loading ? 'Migrando…' : 'Migrar'}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MigrateGroupsToTagsDialog;
